import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Truck, CreditCard, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { categories as categoriesApi, banners as bannersApi, products as productsApi, seed, getImageUrl } from '../lib/api';
import ProductCard from '../components/ProductCard';
import usePageTitle from '../hooks/usePageTitle';

// Category image with skeleton loading effect
const CategoryImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const fallbackSrc = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600';
  
  // Convert local paths to full URLs
  const imageSrc = getImageUrl(src) || fallbackSrc;
  
  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-110 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          e.target.src = fallbackSrc;
          setLoaded(true);
        }}
      />
    </>
  );
};

// Banner Carousel Component
const BannerCarousel = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Auto-slide every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
      setImageLoaded(false);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [banners.length]);
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setImageLoaded(false);
  };
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    setImageLoaded(false);
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
    setImageLoaded(false);
  };
  
  if (banners.length === 0) return null;
  
  const currentBanner = banners[currentIndex];
  
  return (
    <div className="relative w-full overflow-hidden bg-[#0F172A]">
      {/* Banner Image - natural aspect ratio, fully visible (matches main banner) */}
      <div className="relative w-full">
        {!imageLoaded && (
          <div className="w-full aspect-[16/6] bg-gray-200 animate-pulse" />
        )}
        <img
          src={getImageUrl(currentBanner.image_url)}
          alt={currentBanner.title || 'Banner'}
          className={`w-full h-auto object-contain transition-opacity duration-500 ${
            imageLoaded ? 'opacity-100 relative' : 'opacity-0 absolute inset-0'
          }`}
          onLoad={() => setImageLoaded(true)}
        />
        
        {/* Overlay for text visibility */}
        {(currentBanner.title || currentBanner.subtitle) && (
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        )}
      </div>
      
      {/* Banner Content */}
      {(currentBanner.title || currentBanner.subtitle) && (
        <div className="absolute inset-0 flex items-center">
          <div className="container-wide">
            <div className="max-w-xl text-white">
              {currentBanner.title && (
                <h2 className="text-3xl md:text-5xl font-bold mb-4">{currentBanner.title}</h2>
              )}
              {currentBanner.subtitle && (
                <p className="text-lg md:text-xl mb-6 opacity-90">{currentBanner.subtitle}</p>
              )}
              {currentBanner.link && (
                <Link to={currentBanner.link}>
                  <Button size="lg" className="bg-white text-[#002FA7] hover:bg-gray-100">
                    Shop Now <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation Arrows */}
      {banners.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg"
            aria-label="Previous banner"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-lg"
            aria-label="Next banner"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>
        </>
      )}
      
      {/* Dots Indicator */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HomePage = () => {
  usePageTitle('');
  const [categories, setCategories] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const carouselRef = useRef(null);
  const newArrivalsRef = useRef(null);
  const bestSellersRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  // Mouse drag state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftStart, setScrollLeftStart] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // Seed database on first load
        await seed();
      } catch (error) {
        // Ignore if already seeded
      }
      
      // Load categories
      try {
        const response = await categoriesApi.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
      
      // Load banners
      try {
        const response = await bannersApi.getActive();
        setHeroBanners(response.data.banners || []);
      } catch (error) {
        console.error('Failed to load banners:', error);
      }

      // Load new arrivals and best sellers
      try {
        const response = await productsApi.getAll({ limit: 500 });
        const allProducts = response.data.products || [];
        setNewArrivals(allProducts.filter(p => p.is_new).slice(0, 10));
        setBestSellers(allProducts.filter(p => p.is_best_seller).slice(0, 10));
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    init();
  }, []);

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScroll);
      return () => carousel.removeEventListener('scroll', checkScroll);
    }
  }, [categories]);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320; // Width of one card + gap
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeftStart(carouselRef.current.scrollLeft);
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.scrollSnapType = 'none'; // Disable snap while dragging
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Increased scroll speed for smoother feel
    
    // If moved more than 5px, consider it a drag
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    
    carouselRef.current.scrollLeft = scrollLeftStart - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (carouselRef.current) {
      carouselRef.current.style.cursor = 'grab';
      carouselRef.current.style.scrollSnapType = 'x mandatory'; // Re-enable snap after drag
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (carouselRef.current) {
        carouselRef.current.style.cursor = 'grab';
        carouselRef.current.style.scrollSnapType = 'x mandatory'; // Re-enable snap after drag
      }
    }
  };
  
  const handleCategoryClick = (e) => {
    if (hasDragged) {
      e.preventDefault();
      setHasDragged(false);
    }
  };

  // Product carousel scroll helpers
  const scrollProducts = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 320;
      ref.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: Package,
      title: 'Bulk Ordering',
      description: 'Order multiple sizes and quantities in a single transaction'
    },
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Quick shipping to your warehouse or retail location'
    },
    {
      icon: CreditCard,
      title: 'Credit Account',
      description: 'Flexible credit terms for established business partners'
    },
    {
      icon: Users,
      title: 'B2B Focused',
      description: 'Built exclusively for wholesale and retail partners'
    }
  ];

  return (
    <div className="fade-in">
      {/* Hero Section - Dynamic Banners or Default */}
      {heroBanners.length > 0 ? (
        <section className="relative">
          <BannerCarousel banners={heroBanners} />
        </section>
      ) : (
        <section className="relative bg-[#0F172A] text-white overflow-hidden">
          <div className="relative w-full">
            <img
              src="https://customer-assets.emergentagent.com/job_c10de761-bb37-4a59-954e-aa20d6f9f598/artifacts/l079eh9t_banner.png"
              alt="Feel the Connexion Banner"
              className="w-full h-auto object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 pb-8 pt-16">
              <div className="container-wide text-center">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-3 drop-shadow-lg">
                  Premium Wholesale
                  <span className="text-[#4A9FE8]"> Clothing Solutions</span>
                </h1>
                <p className="text-sm md:text-base text-white/90 mb-5 max-w-xl mx-auto drop-shadow-md">
                  Quality garments at competitive wholesale prices. T-shirts, hoodies, caps, and more for your business needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/products">
                    <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white px-6 py-5 text-sm font-semibold" data-testid="browse-products-btn">
                    Browse Products
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0F172A] px-6 py-5 text-sm font-semibold" data-testid="create-account-btn">
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Categories Section - Carousel Style */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="container-wide">
          {/* Header with arrows */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A]">Shop by Category</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`p-2 rounded-full border transition-all ${
                  canScrollLeft 
                    ? 'border-gray-300 hover:border-gray-900 hover:bg-gray-100 cursor-pointer' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`p-2 rounded-full border transition-all ${
                  canScrollRight 
                    ? 'border-gray-300 hover:border-gray-900 hover:bg-gray-100 cursor-pointer' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Category Cards */}
          <div 
            ref={carouselRef}
            className={`flex gap-4 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollBehavior: isDragging ? 'auto' : 'smooth' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.slug}`}
                className="flex-shrink-0 w-[280px] md:w-[300px] group"
                data-testid={`category-${category.slug}`}
                onClick={(e) => handleCategoryClick(e)}
                draggable={false}
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-3">
                  <CategoryImage src={category.image} alt={category.name} />
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                </div>
                <h3 className="text-base font-medium text-[#0F172A] group-hover:underline underline-offset-4">
                  {category.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="container-wide">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A]">New Arrivals</h2>
                <p className="text-sm text-[#64748B] mt-1">The latest additions to our range</p>
              </div>
              <Link to="/products?filter=new" className="text-sm font-medium text-[#002FA7] hover:underline underline-offset-4 hidden sm:block">
                View All <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {newArrivals.slice(0, 8).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Best Sellers Section */}
      {bestSellers.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8FAFC]">
          <div className="container-wide">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#0F172A]">Best Sellers</h2>
                <p className="text-sm text-[#64748B] mt-1">Our most popular products</p>
              </div>
              <Link to="/products" className="text-sm font-medium text-[#002FA7] hover:underline underline-offset-4 hidden sm:block">
                View All <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {bestSellers.slice(0, 8).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">Why Choose Us</h2>
            <p className="text-[#64748B] max-w-xl mx-auto">
              Built for wholesale buyers who demand quality and efficiency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`bg-white p-8 border border-[#E2E8F0] slide-up stagger-${index + 1}`}
              >
                <feature.icon className="w-10 h-10 text-[#002FA7] mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container-wide py-16 md:py-24">
        <div className="bg-[#0F172A] p-8 md:p-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Ordering?
          </h2>
          <p className="text-[#94A3B8] mb-8 max-w-xl mx-auto">
            Create your wholesale account today and get access to competitive pricing and bulk ordering.
          </p>
          <Link to="/register">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white px-8 py-6 text-base font-semibold" data-testid="get-started-btn">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
